/**
 * Property-scoped inbox tab showing email messages received for a property
 *
 * @module property-tabs/PropertyInboxTab
 */

import React from 'react';
import {
  Flex,
  Box,
  Text,
  Select,
  TextField,
  Button,
  LoadingOrbit,
  useToast,
  TagBadge,
} from '@chatsdet/ui';
import { EnvelopeSimpleOpen } from '@phosphor-icons/react';
import {
  usePropertyInboxRecipients,
  usePropertyInboxMessages,
  usePropertyInboxMessageDetail,
  useDeleteInboxMessage,
} from '../../../hooks/usePropertyInbox';
import {
  useOrgDomains,
  useCreateOrgDomain,
  useDeleteOrgDomain,
  type InboxDomain,
} from '../../../hooks/useInboxDomains';
import { MessageList } from '../inbox/MessageList';
import { MessageDetail } from '../inbox/MessageDetail';
import { useDebouncedValue } from '../../../hooks/useDebouncedValue';
import { PropertyTextMessages } from '../messaging/PropertyTextMessages';
import { useAuth } from '../../../contexts/AuthContext';

type PropertyInboxTabProps = {
  propertyId: string;
};

export function PropertyInboxTab({ propertyId }: PropertyInboxTabProps) {
  const { user } = useAuth();
  const smsEnabled = Boolean(user?.features?.smsAgentMessaging);
  const [channel, setChannel] = React.useState<'email' | 'sms'>('email');
  return (
    <Flex direction="column" gap="md">
      <Flex gap="xs">
        <Button
          variant={channel === 'email' ? 'primary' : 'outline'}
          size="sm"
          onPress={() => setChannel('email')}
        >
          Email
        </Button>
        {smsEnabled && (
          <Button
            variant={channel === 'sms' ? 'primary' : 'outline'}
            size="sm"
            onPress={() => setChannel('sms')}
          >
            Text messages
          </Button>
        )}
      </Flex>
      {channel === 'email' || !smsEnabled ? (
        <EmailInboxPanel propertyId={propertyId} />
      ) : (
        <PropertyTextMessages propertyId={propertyId} />
      )}
    </Flex>
  );
}

function EmailInboxPanel({ propertyId }: PropertyInboxTabProps) {
  const [selectedRecipientId, setSelectedRecipientId] = React.useState<string | null>(null);
  const [search, setSearch] = React.useState('');
  const [selectedMessageId, setSelectedMessageId] = React.useState<string | null>(null);
  const [page, setPage] = React.useState(1);

  const debouncedSearch = useDebouncedValue(search.trim(), 400);

  // Fetch recipients for this property
  const recipientsQ = usePropertyInboxRecipients(propertyId);
  const recipients = recipientsQ.data ?? [];

  // Build recipient options for select
  const recipientOptions = React.useMemo(
    () =>
      recipients.map((r) => ({
        id: r.id,
        label: r.label ? `${r.email} (${r.label})` : r.email,
      })),
    [recipients]
  );

  // Auto-select first recipient when data loads
  React.useEffect(() => {
    if (recipientOptions.length === 0) {
      setSelectedRecipientId(null);
      return;
    }
    if (!selectedRecipientId || !recipientOptions.some((opt) => opt.id === selectedRecipientId)) {
      setSelectedRecipientId(recipientOptions[0]?.id ?? null);
    }
  }, [recipientOptions, selectedRecipientId]);

  // Reset page and selection when filters change
  React.useEffect(() => {
    setPage(1);
    setSelectedMessageId(null);
  }, [selectedRecipientId, debouncedSearch]);

  // Fetch messages
  const messagesQ = usePropertyInboxMessages({
    propertyId,
    recipientId: selectedRecipientId,
    search: debouncedSearch.length > 0 ? debouncedSearch : null,
    page,
  });

  const messages = messagesQ.data?.data ?? [];
  const pagination = messagesQ.data?.pagination;

  // Auto-select first message
  React.useEffect(() => {
    if (messages.length === 0) {
      setSelectedMessageId(null);
      return;
    }
    if (!selectedMessageId || !messages.some((m) => m.id === selectedMessageId)) {
      setSelectedMessageId(messages[0]?.id ?? null);
    }
  }, [messages, selectedMessageId]);

  // Fetch message detail
  const detailQ = usePropertyInboxMessageDetail(selectedMessageId);

  // Delete mutation
  const deleteMessage = useDeleteInboxMessage();
  const { toast } = useToast();

  const handleDelete = () => {
    if (!selectedMessageId) return;
    deleteMessage.mutate(selectedMessageId, {
      onSuccess: () => {
        setSelectedMessageId(null);
        toast.success('Message deleted');
      },
      onError: (error) => {
        toast.error(error instanceof Error ? error.message : 'Failed to delete message');
      },
    });
  };

  // Loading state for recipients
  if (recipientsQ.isLoading) {
    return (
      <Flex align="center" justify="center" className="py-12">
        <LoadingOrbit size="lg" />
      </Flex>
    );
  }

  // No recipients configured
  if (recipients.length === 0) {
    return <InboxEmptyState />;
  }

  return (
    <Flex direction="column" gap="md">
      {/* Toolbar */}
      <Flex align="end" gap="md" wrap>
        <Box className="w-[260px]">
          <TextField
            label="Search"
            placeholder="Subject, body, sender..."
            value={search}
            onChange={(value) => setSearch(String(value))}
          />
        </Box>

        {recipientOptions.length > 1 && (
          <Box className="w-[280px]">
            <Select
              label="Mailbox"
              placeholder="All mailboxes"
              items={recipientOptions}
              selectedKey={selectedRecipientId ?? undefined}
              onSelectionChange={(key) => setSelectedRecipientId(String(key))}
            />
          </Box>
        )}

        {pagination && (
          <TagBadge
            name={
              <>
                {pagination.totalItems} message{pagination.totalItems !== 1 ? 's' : ''}
              </>
            }
            color="gray"
            hideDot
            className="self-end mb-1.5"
          />
        )}
      </Flex>

      {/* Two-panel layout */}
      <Flex gap="lg" className="min-h-[520px]">
        {/* Message list */}
        <Box className="w-[360px] max-w-[400px] border border-border/60 rounded-lg overflow-hidden flex flex-col bg-card/40">
          <MessageList
            messages={messages}
            selectedMessageId={selectedMessageId}
            onSelectMessage={setSelectedMessageId}
            isLoading={messagesQ.isLoading}
            isError={messagesQ.isError}
            totalItems={pagination?.totalItems ?? 0}
            page={page}
            totalPages={pagination?.totalPages ?? 0}
            onPageChange={setPage}
            emptyMessage={
              debouncedSearch ? 'No messages match your search.' : 'No messages received yet.'
            }
            showAvatar
          />
        </Box>

        {/* Message detail */}
        <Box className="flex-1 border border-border/60 rounded-lg bg-card/40 p-6 overflow-y-auto">
          {!selectedMessageId ? (
            <Flex align="center" justify="center" className="h-full text-center">
              <Text size="sm" className="text-muted-foreground">
                Select a message to see its contents.
              </Text>
            </Flex>
          ) : detailQ.isLoading ? (
            <Flex align="center" justify="center" className="h-full">
              <LoadingOrbit size="lg" />
            </Flex>
          ) : detailQ.isError ? (
            <Flex align="center" justify="center" className="h-full text-center">
              <Text size="sm" className="text-destructive">
                Unable to load this message.
              </Text>
            </Flex>
          ) : detailQ.data ? (
            <MessageDetail
              message={detailQ.data}
              onDelete={handleDelete}
              isDeleting={deleteMessage.isPending}
            />
          ) : null}
        </Box>
      </Flex>
    </Flex>
  );
}

function InboxEmptyState() {
  const createDomain = useCreateOrgDomain();
  const deleteDomain = useDeleteOrgDomain();
  const orgDomainsQ = useOrgDomains();
  const orgDomains = orgDomainsQ.data ?? [];
  const { toast } = useToast();
  const [senderDomain, setSenderDomain] = React.useState('');

  const handleAddDomain = async () => {
    const domain = senderDomain.trim();
    if (!domain) return;

    try {
      await createDomain.mutateAsync({ domain, description: 'Added from inbox setup' });
      toast.success(`Domain "${domain}" added to the allowlist`);
      setSenderDomain('');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to add domain');
    }
  };

  const handleRemoveDomain = async (domain: InboxDomain) => {
    try {
      await deleteDomain.mutateAsync(domain.id);
      toast.success(`Domain "${domain.domain}" removed`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to remove domain');
    }
  };

  return (
    <Flex direction="column" align="center" justify="center" className="py-16 text-center gap-4">
      <EnvelopeSimpleOpen size={36} className="text-muted-foreground" />
      <Text className="text-muted-foreground">
        No inbox recipients configured for this application.
      </Text>
      <Text size="sm" className="text-muted-foreground max-w-md">
        Inbox recipients are provisioned automatically when you create a credential with a matching
        email domain.
      </Text>

      <Box className="mt-4 p-4 border border-border rounded-lg max-w-md w-full">
        <Text size="sm" weight="medium" className="mb-2 text-left">
          Allow a sender domain
        </Text>
        <Text size="xs" className="text-muted-foreground mb-3 text-left">
          Add the domain your app sends emails from (e.g., notifications.myapp.com) so those emails
          are delivered to inboxes.
        </Text>
        <Flex gap="sm">
          <Box className="flex-1">
            <TextField
              aria-label="Sender domain"
              placeholder="e.g., myapp.com"
              value={senderDomain}
              onChange={setSenderDomain}
            />
          </Box>
          <Button
            variant="primary"
            size="sm"
            onPress={handleAddDomain}
            isDisabled={!senderDomain.trim() || createDomain.isPending}
            className="self-start mt-0.5"
          >
            {createDomain.isPending ? 'Adding...' : 'Add'}
          </Button>
        </Flex>

        {/* List of org domains */}
        {orgDomains.length > 0 ? (
          <Flex direction="column" gap="xs" className="mt-3 pt-3 border-t border-border">
            {orgDomains.map((d) => (
              <Flex key={d.id} align="center" justify="between" className="px-1">
                <Text size="sm">{d.domain}</Text>
                <Button
                  variant="ghost"
                  size="sm"
                  onPress={() => handleRemoveDomain(d)}
                  isDisabled={deleteDomain.isPending}
                  aria-label={`Remove ${d.domain}`}
                  className="text-muted-foreground hover:text-destructive"
                >
                  &times;
                </Button>
              </Flex>
            ))}
          </Flex>
        ) : (
          <Text size="xs" className="text-muted-foreground mt-3">
            No domains added yet.
          </Text>
        )}
      </Box>
    </Flex>
  );
}
